import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useApi } from '@/lib/api'
import { useCodeValues } from '@/hooks/use-code-values'
import { useCode } from '@/contexts/code-context'
import { useFeatureFlags } from '@/contexts/feature-flag-context'
import { buildWorkerViews, type WorkerProfileSummary } from '@/lib/adapters/resource-availability-adapter'
import { addMonths } from '@/lib/utils/timeline-axis'
import type {
  ResourceAvailabilityResponse,
  ResourceAvailabilityRow,
  WorkerCertificationEntry,
  WorkerSkillEntry,
  WorkerView,
} from '@/types/ui/resource-availability'

// Matches PhxConstants.FeatureFlags.T4SalariedWorker in the Phoenix appNext shell. The actual
// FeatureName stored in Cosmos is camel-cased ("t4SalariedWorker") and the flags map is
// case-sensitive, so we key on the camelCase form.
// When ON: T4 Salaried and T4 Hourly are distinct worker types — surface the T4-type label.
// When OFF: every T4 worker is effectively T4 Hourly (per the legacy contact-creation flow),
// so the salaried/hourly distinction carries no meaning — fall through to ProfileType instead.
const T4_SALARIED_WORKER_FLAG = 't4SalariedWorker'

export interface PendingRange {
  /** Inclusive ISO yyyy-MM-dd. Heatmap column whose `monthStart` falls in [startIso, endIso]
   *  is rendered as a skeleton until the fetch completes. */
  startIso: string
  endIso: string
}

export interface UseResourceAvailabilityDataResult {
  data: WorkerView[]
  raw: ResourceAvailabilityResponse | null
  ptoUnavailable: boolean
  /** Initial-load only (no merged data yet). Page-level "Loading…" gate. */
  loading: boolean
  /** Current incremental gap in flight, if any. Triggers per-column skeletons. */
  pendingRange: PendingRange | null
  error: string | null
  refetch: () => void
}

interface PeopleListResponseItem {
  // PeopleService preserves C# PascalCase (.UseMemberCasing()).
  Contact?: {
    FirstName?: string
    LastName?: string
  }
  UserProfile?: {
    UserProfileId?: number
    ProfileTypeCode?: string
    UserProfileWorker?: {
      T4SalariedDetails?: {
        T4TypeCode?: string
        // ProcurementSourceCode for T4 salaried workers; hourly workers carry it on
        // EngagementDetails instead (we honor the T4-first fallback above).
        ProcurementSourceCode?: string
        FunctionalManagerProfileId?: number
      }
      EngagementDetails?: {
        BranchCode?: string
        WorkerLocation?: string
        ProcurementSourceCode?: string
        FunctionalManagerProfileId?: number
      }
      Skills?: Array<{ SkillId: number; ProficiencyLevelId?: number }>
      Certifications?: Array<{ CertificationId: number; IssueDate?: string; ExpiryDate?: string }>
    }
  }
}

// Module-level so it survives component unmount (e.g., navigating to a worker drill-down
// and back) AND survives prop changes within a session. Each new fetch merges into this
// shared store; full-page reload clears.
//
// `peopleRetriesRemaining` budgets one cache-hit recovery attempt per session for the
// case where the initial people-list call failed transiently and left names unresolved
// (workers showing as "Worker {id}"). Without this, the cache-hit early-return path
// would lock those gaps in place until a hard refresh. Bounded so a permanently broken
// people service doesn't trigger an attempt on every effect run.
interface MergedCache {
  response: ResourceAvailabilityResponse | null
  peopleRaw: PeopleListResponseItem[]
  peopleRetriesRemaining: number
}
const PEOPLE_CACHE_HIT_RETRIES = 1
const mergedCache: MergedCache = {
  response: null,
  peopleRaw: [],
  peopleRetriesRemaining: PEOPLE_CACHE_HIT_RETRIES,
}

/**
 * Compute the missing edge (or full range) we need to fetch given the currently-loaded
 * range and the user's requested range.
 * - `null`: requested range is fully contained in loaded range. No fetch.
 * - otherwise: the [start, end] to fetch. The boundary month is shifted by one month so
 *   already-loaded edge months don't flash as skeletons during incremental fetches; the
 *   week containing the boundary day is still fetched on both sides and gets deduped by
 *   (worker, weekStart) row key in the merge step.
 */
export function computeFetchRange(
  loaded: { startMonth: string; endMonth: string } | null,
  requested: { start: string; end: string }
): { start: string; end: string } | null {
  if (!loaded) return requested
  const needLeft = requested.start < loaded.startMonth
  const needRight = requested.end > loaded.endMonth
  if (!needLeft && !needRight) return null
  if (needLeft && needRight) return requested // both edges missing — fetch the union
  // Both edges shift the boundary by a whole month (day 1), so an edge fetch is only real
  // when the shifted boundary stays on the correct side of the requested edge. If it
  // crosses (inverted range), there's nothing genuinely missing — never emit start > end.
  if (needLeft) {
    const end = addMonths(loaded.startMonth, -1)
    return end < requested.start ? null : { start: requested.start, end }
  }
  const start = addMonths(loaded.endMonth, 1)
  return start > requested.end ? null : { start, end: requested.end }
}

/** Snap a (possibly day-precision) ISO date to the first of its month, matching the
 *  month-aligned window contract shared by the API response and the incremental merge. */
export function firstOfMonthIso(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`
}

function rowKey(r: ResourceAvailabilityRow): string {
  return `${r.userProfileId}|${r.bucketStart}`
}

function minIso(a: string, b: string): string {
  return a < b ? a : b
}
function maxIso(a: string, b: string): string {
  return a > b ? a : b
}

/**
 * Merge a freshly-fetched response into the existing merged response. Idempotent on
 * overlap: rows are deduped by (worker, weekStart); dictionaries union with `next`
 * winning on key conflict.
 */
export function mergeResponses(
  prev: ResourceAvailabilityResponse,
  next: ResourceAvailabilityResponse
): ResourceAvailabilityResponse {
  const rowMap = new Map<string, ResourceAvailabilityRow>()
  for (const r of prev.rows) rowMap.set(rowKey(r), r)
  for (const r of next.rows) rowMap.set(rowKey(r), r)

  // Monthly rows must merge too — otherwise a window extension drops them from the merged
  // response, `hasMonthlyRows` goes false, and the whole monthly view silently falls back to
  // weekly aggregation. Deduped by (worker, monthStart) via the same rowKey (month-anchored).
  const monthlyRowMap = new Map<string, ResourceAvailabilityRow>()
  for (const r of prev.monthlyRows ?? []) monthlyRowMap.set(rowKey(r), r)
  for (const r of next.monthlyRows ?? []) monthlyRowMap.set(rowKey(r), r)
  // Presence, not length: a new BE legitimately sends an empty monthlyRows (e.g. all data
  // sits in out-of-window edge days, or the window is fully idle). Treating empty as "absent"
  // would flip hasMonthlyRows off downstream and reactivate the legacy weekly aggregation,
  // leaking out-of-window leading-week days into the monthly view.
  const hasMonthly = prev.monthlyRows !== undefined || next.monthlyRows !== undefined

  const mergeNested = <V,>(
    a: Record<string, Record<string, V>> | undefined,
    b: Record<string, Record<string, V>> | undefined
  ): Record<string, Record<string, V>> | undefined => {
    if (!a && !b) return undefined
    const out: Record<string, Record<string, V>> = { ...(a ?? {}) }
    for (const [k, vMap] of Object.entries(b ?? {})) {
      out[k] = { ...(out[k] ?? {}), ...vMap }
    }
    return out
  }

  // Merge per-worker id lists (union, deduped). Used for the authoritative engagement
  // membership map — must be merged so a range change/expansion doesn't drop it and make
  // the adapter fall back to spans/BHPD/CSV (which can include removed workers).
  const mergeListMap = (
    a: Record<string, number[]> | undefined,
    b: Record<string, number[]> | undefined
  ): Record<string, number[]> | undefined => {
    if (!a && !b) return undefined
    const out: Record<string, number[]> = { ...(a ?? {}) }
    for (const [workerId, ids] of Object.entries(b ?? {})) {
      out[workerId] = Array.from(new Set([...(out[workerId] ?? []), ...ids]))
    }
    return out
  }

  return {
    startMonth: minIso(prev.startMonth, next.startMonth),
    endMonth: maxIso(prev.endMonth, next.endMonth),
    ptoUnavailable: prev.ptoUnavailable || next.ptoUnavailable,
    rows: Array.from(rowMap.values()),
    monthlyRows: hasMonthly ? Array.from(monthlyRowMap.values()) : undefined,
    workerHoursPerDay: { ...(prev.workerHoursPerDay ?? {}), ...(next.workerHoursPerDay ?? {}) },
    // Per-worker and window-independent, so a plain later-wins merge is right. Undefined only
    // when neither response carried the field (older BE) — keep it undefined in that case so
    // the heatmap reads "no end dates known" rather than "nobody has one".
    workerProfileEndDates: prev.workerProfileEndDates || next.workerProfileEndDates
      ? { ...(prev.workerProfileEndDates ?? {}), ...(next.workerProfileEndDates ?? {}) }
      : undefined,
    engagementNamesById: { ...(prev.engagementNamesById ?? {}), ...(next.engagementNamesById ?? {}) },
    workerEngagementBudgetedHoursPerDay: mergeNested(
      prev.workerEngagementBudgetedHoursPerDay,
      next.workerEngagementBudgetedHoursPerDay
    ),
    // A worker's timeline is the same whichever window carried it, so later-wins is right.
    workerEngagementBudgetedHoursTimelines: mergeNested(
      prev.workerEngagementBudgetedHoursTimelines,
      next.workerEngagementBudgetedHoursTimelines
    ),
    workerJobFunctionCodes: { ...(prev.workerJobFunctionCodes ?? {}), ...(next.workerJobFunctionCodes ?? {}) },
    engagementDates: { ...(prev.engagementDates ?? {}), ...(next.engagementDates ?? {}) },
    workerEngagementSpans: mergeNested(prev.workerEngagementSpans, next.workerEngagementSpans),
    workerCurrentEngagementIds: mergeListMap(prev.workerCurrentEngagementIds, next.workerCurrentEngagementIds),
  }
}

export function useResourceAvailabilityData(
  startMonth: string,
  endMonth: string
): UseResourceAvailabilityDataResult {
  const { engagementApi, peopleApi } = useApi()
  const { getCodeValueTextByCode } = useCodeValues()
  // Subscribe to the live code-values object from the context, not just `isReady`. The
  // global CodeProvider clears window.PhoenixCodeValues at the start of every reload (e.g.
  // on HMR or language switch); useCodeValues' isReady stays true through that gap, so it
  // alone won't re-trigger memos that depend on resolved code text. `codeValues` updates
  // synchronously when codes finish (re)loading, giving us a reliable dep.
  const { codeValues } = useCode()
  const { isFeatureEnabled } = useFeatureFlags()
  const t4SalariedDistinctionEnabled = isFeatureEnabled(T4_SALARIED_WORKER_FLAG)

  const [response, setResponse] = useState<ResourceAvailabilityResponse | null>(mergedCache.response)
  const [peopleRaw, setPeopleRaw] = useState<PeopleListResponseItem[]>(mergedCache.peopleRaw)
  // Manager IDs we've already attempted to resolve — prevents re-firing the manager
  // pre-fetch on every render and stops a hypothetical manager-of-manager chain from
  // ever looping (managers fetched as profiles will still surface their *own* manager
  // ID; we resolve those once and stop).
  const attemptedManagerIdsRef = useRef<Set<number>>(new Set())
  const [pendingRange, setPendingRange] = useState<PendingRange | null>(null)
  const [loading, setLoading] = useState(mergedCache.response === null)
  const [error, setError] = useState<string | null>(null)
  const [refetchKey, setRefetchKey] = useState(0)

  // Keep refs in sync so the effect can read the latest merged state without listing
  // them as dependencies (which would cause infinite re-runs when state updates inside
  // the effect's load function).
  const responseRef = useRef(response)
  const peopleRawRef = useRef(peopleRaw)
  responseRef.current = response
  peopleRawRef.current = peopleRaw

  useEffect(() => {
    let cancelled = false

    // The pickers hand us day-precision dates (e.g. "2026-06-30"), but the data window is
    // month-bucketed end to end: the API echoes month-aligned boundaries, defaultMonthWindow
    // emits first-of-month, and the incremental merge shifts edges by whole months. Compare
    // and fetch on that same month precision — otherwise a late-month "from" day (Jun 30)
    // reads as < the response's snapped first-of-month (Jul 01), fabricating a left-edge gap
    // whose computed end lands before the start and 400s the API.
    const requested = { start: firstOfMonthIso(startMonth), end: firstOfMonthIso(endMonth) }
    const loadedRange = responseRef.current
      ? { startMonth: responseRef.current.startMonth, endMonth: responseRef.current.endMonth }
      : null
    const fetchRange = computeFetchRange(loadedRange, requested)

    if (fetchRange === null) {
      // Fully covered by merged data. No fetch needed.
      setPendingRange(null)
      setLoading(false)
      setError(null)

      // One-shot recovery: if the initial people-list call failed (or returned only a
      // subset), peopleRaw has gaps relative to the loaded availability response. Take
      // one more swing at those missing IDs so users aren't permanently stuck with
      // "Worker {id}" labels after a transient blip. Bounded by PEOPLE_CACHE_HIT_RETRIES.
      if (mergedCache.peopleRetriesRemaining > 0 && responseRef.current) {
        const haveIds = new Set(
          peopleRawRef.current
            .map(p => p?.UserProfile?.UserProfileId)
            .filter((n): n is number => typeof n === 'number')
        )
        const responseIds = Object.keys(responseRef.current.workerHoursPerDay ?? {})
          .map(Number)
          .filter(Number.isFinite)
        const missingIds = responseIds.filter(id => !haveIds.has(id))
        if (missingIds.length > 0) {
          mergedCache.peopleRetriesRemaining -= 1
          peopleApi
            .post<PeopleListResponseItem[]>('/people/people-list', missingIds)
            .then(more => {
              if (cancelled || !more) return
              const merged = [...peopleRawRef.current, ...more]
              mergedCache.peopleRaw = merged
              setPeopleRaw(merged)
            })
            .catch(err => {
              // Same best-effort posture as the in-flow people fetch. Budget consumed
              // by the attempt itself, success or failure — we don't ping a broken
              // service repeatedly.
              console.warn('Cache-hit people-list retry failed', err)
            })
        }
      }
      return
    }

    const isInitial = responseRef.current === null
    setPendingRange({ startIso: fetchRange.start, endIso: fetchRange.end })
    if (isInitial) setLoading(true)
    setError(null)

    const load = async () => {
      try {
        const result = await engagementApi.get<ResourceAvailabilityResponse>(
          `/api/ResourceAvailability?startMonth=${fetchRange.start}&endMonth=${fetchRange.end}`
        )
        if (cancelled) return

        // Determine which worker profiles we still need to fetch — only fetch those new
        // to this incremental edge (an idle worker discovered in a wider window is the
        // only common case of a "new" id past the initial load).
        const existingIds = new Set(
          peopleRawRef.current
            .map(p => p?.UserProfile?.UserProfileId)
            .filter((n): n is number => typeof n === 'number')
        )
        const allIds = new Set<number>([
          ...existingIds,
          ...Object.keys(result.workerHoursPerDay ?? {})
            .map(Number)
            .filter(Number.isFinite),
        ])
        // Fall back to deriving from rows for older BE responses without workerHoursPerDay.
        if (Object.keys(result.workerHoursPerDay ?? {}).length === 0) {
          for (const r of result.rows) allIds.add(r.userProfileId)
        }
        const newIds = Array.from(allIds).filter(id => !existingIds.has(id))

        let mergedPeople = peopleRawRef.current
        if (newIds.length > 0) {
          try {
            const more =
              (await peopleApi.post<PeopleListResponseItem[]>('/people/people-list', newIds)) ?? []
            if (cancelled) return
            mergedPeople = [...peopleRawRef.current, ...more]
          } catch (peopleErr) {
            // Names are nice-to-have. Fall back to "Worker {id}".
            console.warn('Failed to fetch additional worker profiles', peopleErr)
          }
        }

        const nextMerged = responseRef.current
          ? mergeResponses(responseRef.current, result)
          : result

        mergedCache.response = nextMerged
        mergedCache.peopleRaw = mergedPeople
        setResponse(nextMerged)
        setPeopleRaw(mergedPeople)
        setPendingRange(null)
        setLoading(false)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load resource availability data')
          if (isInitial) {
            setResponse(null)
            setPeopleRaw([])
          }
          setPendingRange(null)
          setLoading(false)
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [engagementApi, peopleApi, startMonth, endMonth, refetchKey])

  // Pre-fetch manager profiles so the filter pane can show real names instead of raw
  // UserProfileIds. Managers are typically internal staff (no UserProfileWorker, no
  // engagements) so they don't end up in peopleRaw via the normal worker fetch — without
  // this, the manager filter would expose only opaque numeric codes.
  useEffect(() => {
    if (peopleRaw.length === 0) return
    const haveIds = new Set(
      peopleRaw.map(p => p?.UserProfile?.UserProfileId).filter((n): n is number => typeof n === 'number')
    )
    const missing: number[] = []
    for (const p of peopleRaw) {
      const w = p?.UserProfile?.UserProfileWorker
      const mgrId = w?.T4SalariedDetails?.FunctionalManagerProfileId ?? w?.EngagementDetails?.FunctionalManagerProfileId
      if (typeof mgrId !== 'number') continue
      if (haveIds.has(mgrId)) continue
      if (attemptedManagerIdsRef.current.has(mgrId)) continue
      missing.push(mgrId)
    }
    if (missing.length === 0) return
    let cancelled = false
    for (const id of missing) attemptedManagerIdsRef.current.add(id)
    ;(async () => {
      try {
        const more = await peopleApi.post<PeopleListResponseItem[]>('/people/people-list', missing)
        if (cancelled || !more) return
        // Merge against `peopleRawRef.current`, NOT the closure's `peopleRaw`. A range
        // expansion (or any other peopleRaw update) firing during this fetch would have
        // already pushed new entries into state; using the closure base would clobber them
        // and silently drop their workerRole / jobFunctions, breaking the filter options.
        const merged = [...peopleRawRef.current, ...more]
        mergedCache.peopleRaw = merged
        setPeopleRaw(merged)
      } catch (err) {
        // Best-effort: unresolved managers fall back to the raw ID in the filter pane.
        console.warn('Failed to fetch functional-manager profiles', err)
      }
    })()
    return () => { cancelled = true }
  }, [peopleRaw, peopleApi])

  // Profile map is derived (not cached) so it transparently picks up code-value text once
  // the global code cache populates — no refetch required.
  const profiles = useMemo(() => {
    const map = new Map<number, WorkerProfileSummary>()
    const jobFunctionsByWorker = response?.workerJobFunctionCodes ?? {}
    for (const p of peopleRaw) {
      // PeopleService is configured with Newtonsoft.UseMemberCasing(), so the response
      // is PascalCase (Contact / UserProfile / FirstName), not camelCase.
      const userProfileId = p?.UserProfile?.UserProfileId
      if (!userProfileId) continue
      const first = p.Contact?.FirstName ?? ''
      const last = p.Contact?.LastName ?? ''
      const fullName = `${first} ${last}`.trim()

      const t4Code = p.UserProfile?.UserProfileWorker?.T4SalariedDetails?.T4TypeCode
      const profileCode = p.UserProfile?.ProfileTypeCode
      // T4 sub-type only carries meaning when T4SalariedWorker is on. Otherwise the salaried
      // option doesn't exist in this tenant and we'd be showing a label ("T4 Hourly") that
      // every worker shares — the higher-level ProfileType label is more informative.
      const workerRole = (t4SalariedDistinctionEnabled && t4Code)
        ? getCodeValueTextByCode(t4Code, 'usr.CodeProfileWorkerT4Type')
        : profileCode
          ? getCodeValueTextByCode(profileCode, 'usr.CodeProfileType')
          : undefined

      const codes = jobFunctionsByWorker[String(userProfileId)] ?? []
      const jobFunctions = codes
        .map(c => getCodeValueTextByCode(c, 'workorder.CodeJobFunction') ?? c)
        .filter((v): v is string => !!v)

      const skills: WorkerSkillEntry[] = (p.UserProfile?.UserProfileWorker?.Skills ?? [])
        .filter(entry => entry?.SkillId !== undefined)
        .map(entry => ({ skillId: entry.SkillId, proficiencyLevelId: entry.ProficiencyLevelId }))
      const certifications: WorkerCertificationEntry[] = (p.UserProfile?.UserProfileWorker?.Certifications ?? [])
        .filter(entry => entry?.CertificationId !== undefined)
        .map(entry => ({ certificationId: entry.CertificationId, issueDate: entry.IssueDate, expiryDate: entry.ExpiryDate }))

      // Branch / worker location / procurement source are profile-level codes. Branch and
      // worker location live on EngagementDetails. ProcurementSourceCode prefers
      // T4SalariedDetails (T4 salaried workers carry it there); falls back to
      // EngagementDetails for hourly workers — mirrors PeopleSearchHelper.cs:411–415.
      const engagementDetails = p.UserProfile?.UserProfileWorker?.EngagementDetails
      const t4Details = p.UserProfile?.UserProfileWorker?.T4SalariedDetails
      const branchCode = engagementDetails?.BranchCode || undefined
      const workerLocation = engagementDetails?.WorkerLocation || undefined
      const procurementSourceCode =
        t4Details?.ProcurementSourceCode || engagementDetails?.ProcurementSourceCode || undefined
      // Functional manager: T4 first, EngagementDetails fallback — same hierarchy as
      // ProcurementSourceCode. Stored as the raw UserProfileId; the adapter resolves the
      // display name from the profiles map after manager profiles are pre-fetched below.
      const functionalManagerProfileId =
        t4Details?.FunctionalManagerProfileId ?? engagementDetails?.FunctionalManagerProfileId

      map.set(userProfileId, {
        userProfileId,
        workerName: fullName || `Worker ${userProfileId}`,
        workerRole,
        jobFunctions: jobFunctions.length > 0 ? jobFunctions : undefined,
        skills: skills.length > 0 ? skills : undefined,
        certifications: certifications.length > 0 ? certifications : undefined,
        branchCode,
        workerLocation,
        procurementSourceCode,
        functionalManagerProfileId,
      })
    }
    return map
    // `codeValues` re-triggers when the global code cache (re)loads so any "" results from
    // a transient cache-clear get replaced with real text.
  }, [peopleRaw, response, getCodeValueTextByCode, codeValues, t4SalariedDistinctionEnabled])

  const data = useMemo(
    () => (response ? buildWorkerViews(response, profiles) : []),
    [response, profiles]
  )

  const refetch = useCallback(() => {
    mergedCache.response = null
    mergedCache.peopleRaw = []
    mergedCache.peopleRetriesRemaining = PEOPLE_CACHE_HIT_RETRIES
    attemptedManagerIdsRef.current = new Set()
    setResponse(null)
    setPeopleRaw([])
    setRefetchKey(k => k + 1)
  }, [])

  return {
    data,
    raw: response,
    ptoUnavailable: response?.ptoUnavailable ?? false,
    loading,
    pendingRange,
    error,
    refetch,
  }
}
